import React, { useState, useEffect, useCallback } from "react";
import { Upload } from "lucide-react";
import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels, commonButtonLabels, commonMiscLabels } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useContract } from "@/hooks/useContract";
import { useSellerContract } from "@/hooks/useSellerContract";
import { AdminSellerContract } from "@/types/sellerContract";
import { useListing } from "@/hooks/useListing";
import { ListingService } from "@/services/listing/listingService";
import { ListingOverview } from "@/types/listing";
import {
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  SUPPORTED_FILE_TYPES,
} from "@/config/constants";
import { Contract } from "@/types/payment";
import { toast } from "sonner";
import { ApiError } from "@/types/carAuctionClient";

function getApiErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as ApiError).message;
    if (typeof message === "string") return message;
  }
  if (error instanceof Error) return error.message;
  return "";
}

interface ContractUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicleCardId?: string; // If provided, skip listing selection
  listingId?: string; // If provided, use this listing directly
  regNumber?: string; // If provided, fetch listing by reg number
  onSuccess?: (contract: Contract) => void;
  /** "buyer" (default) uploads a buyer Contract; "seller" uploads a SellerContract. */
  party?: "buyer" | "seller";
  onSellerContractCreated?: (contract: AdminSellerContract) => void;
}

interface FormData {
  file: File | null;
  id: string;
  listing: string;
  listingObj: ListingOverview | null;
}

export const ContractUploadDialog: React.FC<ContractUploadDialogProps> = ({
  open,
  onOpenChange,
  vehicleCardId,
  listingId,
  regNumber,
  onSuccess,
  party = "buyer",
  onSellerContractCreated,
}) => {
  const { language } = useLanguage();
  const economyLabels = adminLabels(language).economy;
  const isSeller = party === "seller";
  const cu = {
    ...(economyLabels?.contractUpload ?? {}),
    ...(isSeller ? (economyLabels?.sellerContractUpload ?? {}) : {}),
  } as Record<string, string>;
  const commonButtons = commonButtonLabels(language);
  const commonMisc = commonMiscLabels(language);
  const { createContractAsync: createBuyerContractAsync, isCreatingContract: isCreatingBuyerContract } =
    useContract();
  const {
    createSellerContractAsync,
    isCreatingSellerContract,
  } = useSellerContract();
  const isCreatingContract = isSeller
    ? isCreatingSellerContract
    : isCreatingBuyerContract;
  const { getListingsWithFilters } = useListing();

  // Fetch listing by regNumber only when regNumber prop is provided
  const listingByRegNumberQuery = getListingsWithFilters(
    { regNumber: regNumber! },
    { enabled: !!regNumber },
  );
  const listingByRegNumber = regNumber
    ? listingByRegNumberQuery.data?.listings?.[0]
    : undefined;
  const isLoadingListing = listingByRegNumberQuery.isLoading;

  const [form, setForm] = useState<FormData>({
    file: null,
    id: "",
    listing: "",
    listingObj: null,
  });

  const [touched, setTouched] = useState({
    id: false,
    listing: false,
  });

  const [fileError, setFileError] = useState("");
  const [formError, setFormError] = useState("");
  const [listingQuery, setListingQuery] = useState("");
  const [listingDropdown, setListingDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState<ListingOverview[]>([]);
  const [isSearchingListings, setIsSearchingListings] = useState(false);
  const [totalSearchResults, setTotalSearchResults] = useState(0);
  const [currentSearchPage, setCurrentSearchPage] = useState(1);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setForm({
        file: null,
        id: "",
        listing: "",
        listingObj: null,
      });
      setTouched({
        id: false,
        listing: false,
      });
      setFileError("");
      setFormError("");
      setListingQuery("");
      setListingDropdown(false);
      setSearchResults([]);
      setTotalSearchResults(0);
      setCurrentSearchPage(1);
    }
  }, [open]);

  // Load initial COMPLETED listings when input is focused (no search)
  const handleLoadInitialListings = useCallback(async () => {
    setIsSearchingListings(true);
    try {
      const { ListingManagementService } =
        await import("@/services/listing/listingManagementService");
      const results = await ListingManagementService.getAllListings({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        status: (isSeller ? undefined : "completed") as any, // Backend expects 'COMPLETED' but frontend type is lowercase; seller contracts may be created for any listing
        page: 1,
        limit: 5,
      }); // Backend returns 'data' not 'listings'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const listings = results.listings || (results as any).data || [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const totalCount = results.totalCount || (results as any).count || 0;
      setSearchResults(listings);
      setTotalSearchResults(totalCount);
      setCurrentSearchPage(1);
      setListingDropdown(true);
    } catch (error) {
      setFormError(cu.errorLoadingListings ?? "Error loading listings");
    } finally {
      setIsSearchingListings(false);
    }
  }, [cu.errorLoadingListings, isSeller]);

  // Search listings with search term
  const handleSearchListings = useCallback(
    async (page: number = 1, limit: number = 5, append: boolean = false) => {
      if (!listingQuery.trim()) {
        // If no search term, load initial listings
        await handleLoadInitialListings();
        return;
      }

      setIsSearchingListings(true);
      try {
        // Use admin listings endpoint for admin users
        const { ListingManagementService } =
          await import("@/services/listing/listingManagementService");
        const results = await ListingManagementService.getAllListings({
          search: listingQuery,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          status: (isSeller ? undefined : "completed") as any, // Backend expects 'COMPLETED' but frontend type is lowercase; seller contracts may be created for any listing
          page,
          limit,
        }); // Backend returns 'data' not 'listings'
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const listings = results.listings || (results as any).data || [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const totalCount = results.totalCount || (results as any).count || 0;

        if (append) {
          setSearchResults((prev) => [...prev, ...listings]);
        } else {
          setSearchResults(listings);
        }

        setTotalSearchResults(totalCount);
        setCurrentSearchPage(page);
        setListingDropdown(true);
      } catch (error) {
        setFormError(cu.errorSearchingListings ?? "Error searching listings");
      } finally {
        setIsSearchingListings(false);
      }
    },
    [listingQuery, handleLoadInitialListings, cu.errorSearchingListings, isSeller],
  );

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;

    if (name === "file" && files && files[0]) {
      const file = files[0];
      setFileError("");

      // Validate file type
      const isValidType = SUPPORTED_FILE_TYPES.some((type) => {
        if (type === "image/jpeg" || type === "image/png") {
          return file.type.startsWith("image/");
        }
        return file.type === type;
      });

      if (!isValidType) {
        setFileError(
          (cu.onlyAllowed ?? "Only {types} allowed").replace(
            "{types}",
            SUPPORTED_FILE_TYPES.join(", "),
          ),
        );
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileError(
          (cu.fileTooLarge ?? "File too large").replace(
            "{mb}",
            String(MAX_FILE_SIZE_MB),
          ),
        );
        return;
      }

      setForm((prev) => ({ ...prev, file }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    // Clear form error when user starts typing
    if (formError) {
      setFormError("");
    }
  };

  const handleLoadMore = () => {
    const nextPage = currentSearchPage + 1;
    handleSearchListings(nextPage, 5, true); // Load next 5 results
  };

  const isFormValid =
    form.file &&
    form.id.trim() &&
    (listingId ||
      (regNumber && listingByRegNumber?.id) ||
      (form.listing.trim() && form.listingObj)) &&
    !fileError;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    if (!isFormValid) {
      setFormError(cu.pleaseFillAll ?? "Please fill in all required fields");
      return;
    }

    try {
      const finalListingId =
        listingId ||
        (regNumber ? listingByRegNumber?.id : undefined) ||
        form.listingObj?.id;

      if (!finalListingId) {
        setFormError(cu.noListingSelected ?? "No listing selected");
        return;
      }

      const createContract = async (replaceExisting: boolean) => {
        const request = {
          listingId: finalListingId,
          contractNumber: form.id,
          contractFile: form.file!,
        };
        if (isSeller) {
          return createSellerContractAsync({ request, replaceExisting });
        }
        return createBuyerContractAsync({ request, replaceExisting });
      };

      // Check if contract already exists and ask for confirmation
      let createdContract: Contract | AdminSellerContract | null = null;
      try {
        // Try to create without replacing first to check if it exists
        createdContract = await createContract(false);

        // Success toast
        toast.success(cu.created ?? "Contract created successfully!");
      } catch (error: unknown) {
        if (getApiErrorMessage(error).includes("already exists")) {
          const confirmReplace = window.confirm(
            cu.replaceConfirm ??
              "A contract already exists for this listing. Replace it?",
          );
          if (!confirmReplace) {
            return;
          }

          // Try again with replaceExisting = true
          createdContract = await createContract(true);

          // Success toast for replacement
          toast.success(cu.updated ?? "Contract updated successfully!");
        } else {
          // For other errors, throw to be caught by outer catch
          throw error;
        }
      }

      // Only call onSuccess and close dialog if contract was successfully created
      if (createdContract) {
        if (isSeller) {
          onSellerContractCreated?.(createdContract as AdminSellerContract);
        } else {
          onSuccess?.(createdContract as Contract);
        }
        onOpenChange(false);
      }
    } catch (error) {
      const errorMessage =
        getApiErrorMessage(error) ||
        (commonMisc?.unknownError ?? "Unknown error");
      setFormError(
        `${cu.createError ?? "Error creating contract"}: ${errorMessage}`,
      );
      toast.error(
        `${cu.createError ?? "Error creating contract"}: ${errorMessage}`,
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md px-4">
        <DialogHeader>
          <DialogTitle>{cu.title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <p className={`block text-sm font-medium mb-1`}>
              {cu.contractFile}
            </p>
            <FileUploadTrigger
              accept="application/pdf,image/*"
              onFilesSelected={(files) => {
                if (!files[0]) return;
                const dt = new DataTransfer();
                dt.items.add(files[0]);
                handleFormChange({
                  target: { name: "file", files: dt.files },
                } as React.ChangeEvent<HTMLInputElement>);
              }}
            >
              {(open) => (
                <button
                  type="button"
                  className={`flex items-center gap-2 px-4 py-2 rounded border w-full justify-center font-medium transition-colors ${
                    fileError
                      ? "border-red-500 text-red-600"
                      : "border-primary text-primary hover:bg-primary/10"
                  } cursor-pointer`}
                  onClick={open}
                >
                  <Upload className="h-5 w-5" />
                  {form.file ? cu.changeFile : cu.selectFile}
                </button>
              )}
            </FileUploadTrigger>
            {form.file && (
              <span className="text-xs mt-1 block">{form.file.name}</span>
            )}
            {fileError && (
              <div className="text-red-500 text-xs mt-1">{fileError}</div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {cu.contractNumber}
            </label>
            <Input
              name="id"
              value={form.id}
              onChange={handleFormChange}
              required
              className={touched.id && !form.id.trim() ? "border-red-500" : ""}
              onBlur={() => setTouched((t) => ({ ...t, id: true }))}
            />
          </div>

          {/* Only show listing selection if no listingId or regNumber provided */}
          {!listingId && !regNumber && (
            <div className="relative">
              <label className="block text-sm font-medium mb-1">
                {cu.listing}
              </label>
              <div className="flex gap-2">
                <Input
                  name="listing"
                  autoComplete="off"
                  value={listingQuery}
                  onChange={(e) => {
                    setListingQuery(e.target.value);
                    setForm((f) => ({
                      ...f,
                      listing: e.target.value,
                      listingObj: null,
                    }));
                  }}
                  placeholder={cu.listingSearchPlaceholder}
                  required
                  className={
                    touched.listing && !form.listing.trim()
                      ? "border-red-500"
                      : ""
                  }
                  onFocus={async () => {
                    if (searchResults.length === 0) {
                      // Load initial COMPLETED listings when input is focused
                      await handleLoadInitialListings();
                    } else {
                      setListingDropdown(true);
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleSearchListings(1, 5, false)}
                  disabled={isSearchingListings}
                  className="px-3"
                >
                  {isSearchingListings ? cu.searching : cu.search}
                </Button>
              </div>
              {(() => {
                return null;
              })()}
              {listingDropdown && searchResults.length > 0 && (
                <div className="absolute bg-card z-10 border rounded w-full mt-1 shadow max-h-60 overflow-y-auto">
                  <ul>
                    {searchResults.map((listing, index) => (
                      <li
                        key={listing.id || index}
                        className="px-3 py-2 hover:bg-accent/10 cursor-pointer border-b last:border-b-0"
                        onMouseDown={() => {
                          const label = `${
                            listing.vehicleDetails?.regNumber || ""
                          } - ${listing.title}`;
                          setForm((f) => ({
                            ...f,
                            listing: label,
                            listingObj: listing,
                          }));
                          setListingQuery(label);
                          setListingDropdown(false);
                        }}
                      >
                        {listing.vehicleDetails?.regNumber || ""} -{" "}
                        {listing.title}
                      </li>
                    ))}
                  </ul>
                  {searchResults.length < totalSearchResults && (
                    <button
                      type="button"
                      onClick={handleLoadMore}
                      disabled={isSearchingListings}
                      className="w-full px-3 py-2 text-sm text-primary hover:bg-accent/10 border-t"
                    >
                      {isSearchingListings
                        ? cu.loadingMore
                        : (
                            cu.loadMoreRemaining ??
                            "Show more... ({count} remaining)"
                          ).replace(
                            "{count}",
                            String(totalSearchResults - searchResults.length),
                          )}
                    </button>
                  )}
                </div>
              )}
              {listingDropdown &&
                searchResults.length === 0 &&
                !isSearchingListings &&
                listingQuery.trim() && (
                  <div className="absolute bg-card z-10 border rounded w-full mt-1 shadow px-3 py-2 text-sm text-muted-foreground">
                    {cu.noResults}
                  </div>
                )}
            </div>
          )}

          {formError && <div className="text-red-500 text-sm">{formError}</div>}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              type="button"
              onClick={() => onOpenChange(false)}
            >
              {commonButtons?.cancel ?? "Cancel"}
            </Button>
            <Button type="submit" disabled={!isFormValid || isCreatingContract}>
              {isCreatingContract ? cu.creating : cu.submit}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
